import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, IconButton } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import type { LoginStatusFilter } from "./types";

type Props = {
	value: LoginStatusFilter;
	onChange: (value: LoginStatusFilter) => void;
	onRemove: () => void;
};

/**
 * The removable "Login status" filter row: a caption + value + chevron chip with a bin beside it —
 * the shape the broadcast composer's filter rows already use.
 *
 * The chip is a `SystemDropdown` `button` trigger whose `label` carries the caption, so the two
 * options get the house panel, keyboard handling and tick marks instead of a hand-rolled popover.
 */
export const LoginStatusFilterChip: FC<Props> = ({ value, onChange, onRemove }) => {
	const { t } = useTranslation();

	const items = useMemo<DropdownItem<LoginStatusFilter>[]>(
		() => [
			{ value: "never", label: t("team.resend_welcome.login_status.never"), id: "never" },
			{ value: "all", label: t("team.resend_welcome.login_status.all"), id: "all" },
		],
		[t],
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			<SystemDropdown
				data-testid="resend-welcome-login-status"
				items={items}
				selectedItem={value}
				onChange={(next) => next && onChange(next)}
				trigger="button"
				label={t("team.resend_welcome.login_status.caption")}
				valueToString={(next) => (next === "never" ? t("team.resend_welcome.login_status.never") : t("team.resend_welcome.login_status.all"))}
				selectSx={(theme) => ({
					height: 38,
					px: 1.5,
					gap: 1.25,
					fontSize: "14px",
					color: INK.PRIMARY,
					backgroundColor: alpha(theme.palette.primary.main, 0.04),
					// The caption is the field name, so it recedes the way a floating label does on the field variant.
					"& > span:first-of-type": { fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, flexShrink: 0 },
					// The value is two short words; letting it ellipsize turns "Never logged in" into
					// "Never logge…", which is the one thing this chip exists to state.
					"& > div": { overflow: "visible", "& > span": { overflow: "visible", textOverflow: "clip" } },
				})}
			/>
			<IconButton
				data-testid="resend-welcome-login-status-remove"
				aria-label={t("team.resend_welcome.login_status.remove")}
				onClick={onRemove}
				size="small"
				sx={{ color: INK.MUTED }}
			>
				<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
			</IconButton>
		</Box>
	);
};
